/**
 * `telegram/session.ts` — the Mini App's sign-in (#2074).
 *
 * `POST /api/telegram/miniapp/session` with the signed `initData` returns a
 * twelve-hour `tgapp` token. The token lives in this module only — never
 * localStorage or sessionStorage — so it dies with the webview, and a fresh
 * open signs in again from Telegram's fresh `initData`.
 */
import { ApiError, apiFetch } from "../api/client";
import type { paths } from "../api/schema.gen";

type MiniAppLinkUrl =
  paths["/api/telegram/miniapp/link-url"]["post"]["responses"]["200"]["content"]["application/json"];

type MiniAppSession =
  paths["/api/telegram/miniapp/session"]["post"]["responses"]["200"]["content"]["application/json"];

let tgappToken: string | null = null;

export function getTgappToken(): string | null {
  return tgappToken;
}

export function setTgappToken(token: string | null): void {
  tgappToken = token;
}

/** The `Authorization` value for the Mini App session, or `null`. */
export function tgappAuthValue(token: string | null = tgappToken): string | null {
  return token ? `Bearer tgapp:${token}` : null;
}

/** Why the Mini App could not sign in — each has its own one line. */
export type MiniAppSessionProblem =
  | "not_linked"
  | "telegram_off"
  | "no_init_data"
  | "invalid_init_data"
  | "failed";

export type MiniAppSessionResult =
  | { kind: "ready"; token: string; displayName: string }
  | { kind: "problem"; problem: MiniAppSessionProblem };

export async function startMiniAppSession(initData: string | null): Promise<MiniAppSessionResult> {
  if (!initData) return { kind: "problem", problem: "no_init_data" };
  try {
    const session = await apiFetch<MiniAppSession>("/api/telegram/miniapp/session", {
      method: "POST",
      body: JSON.stringify({ init_data: initData }),
    });
    setTgappToken(session.token);
    return { kind: "ready", token: session.token, displayName: session.account.display_name };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.status === 404 && err.detail === "not_linked") {
        return { kind: "problem", problem: "not_linked" };
      }
      if (err.status === 404) return { kind: "problem", problem: "telegram_off" };
      if (err.status === 400) return { kind: "problem", problem: "invalid_init_data" };
    }
    return { kind: "problem", problem: "failed" };
  }
}

/**
 * #2071 — the not-connected screen's one button: a single-use
 * `/connect/telegram?t=<token>` link bound to this Telegram chat. The page
 * opens it in the member's own browser; signing in there connects the chat.
 * `null` when the server says no (stale `initData`, Telegram off, offline).
 */
export async function requestConnectLink(initData: string | null): Promise<string | null> {
  if (!initData) return null;
  try {
    const body = await apiFetch<MiniAppLinkUrl>("/api/telegram/miniapp/link-url", {
      method: "POST",
      body: JSON.stringify({ init_data: initData }),
    });
    return body.url;
  } catch {
    return null;
  }
}
